import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import MainNavbar from "./components/Common/NavBar/MainNavbar";
import Footer from "./components/Common/Footer";
import FeedbackHub from "./components/Common/Feedback/FeedbackHub";
import PrivateRoute from "./components/Common/PrivateRoute";
import AdminLayout from "./components/Admin/AdminLayout";
import SuperAdminDashboard from "./components/Admin/SuperAdminDashboard";
import BinderHome from "./components/Binder/BinderHome";
import PublicBinder from "./components/Binder/PublicBinder";
import CommunityHome from "./components/Community/CommunityHome";
import CommunityDirectory from "./components/Community/CommunityDirectory";
import StoreRegister from "./components/Stores/StoreRegister";
import TournamentsHome from "./components/Tournaments/TournamentsHome";
import TournamentDetail from "./components/Tournaments/TournamentDetail";
import FeedHome from "./components/Feed/FeedHome";
import SearchResults from "./components/SearchResults";
import LoginForm from "./components/Auth/LoginForm";
import RegisterForm from "./components/Auth/RegisterForm";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import VerifyEmail from "./components/Auth/VerifyEmail";
import Onboarding from "./components/Auth/Onboarding";
import ProfileHome from "./components/Profile/ProfileHome";
import TradesHome from "./components/Trades/TradesHome";
import MessagesHome from "./components/Messages/MessagesHome";
import NotificationsHome from "./components/Notifications/NotificationsHome";
import OpportunitiesHome from "./components/Opportunities/OpportunitiesHome";
import MeetupsHome from "./components/Meetups/MeetupsHome";
import ReportsHome from "./components/Reports/ReportsHome";
import CookieConsent from "./components/Legal/CookieConsent";
import LegalPage from "./components/Legal/LegalPage";
import Home from "./pages/Home";
import AuthTokenSync from "./auth/AuthTokenSync";
import { isAuth0Enabled } from "./auth/auth0Config";
import api from "./api/axios";

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!token || !user?.id) return;

    const today = new Date().toLocaleDateString("en-CA");
    const storageKey = `cardora_session_day:${user.id}`;
    if (localStorage.getItem(storageKey) === today) return;

    api.post("/analytics/events", { eventType: "session_started", metadata: { source: "web" } })
      .then(() => localStorage.setItem(storageKey, today))
      .catch(() => {});
  }, [location.pathname]);

  return (
    <div className="app-shell">
      {isAuth0Enabled && <AuthTokenSync />}
      <MainNavbar />
      <FeedbackHub />
      <main className="app-content">
        <Routes>
        <Route path="/" element={<FeedHome isHome />} />
        <Route path="/landing" element={<Home />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />
        <Route path="/legal/:page" element={<LegalPage />} />
        <Route path="/feed" element={<FeedHome />} />
        <Route path="/reports" element={<ReportsHome />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/tournaments" element={<TournamentsHome />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentDetail />} />
        <Route path="/communities" element={<CommunityDirectory />} />
        <Route path="/meetups" element={<MeetupsHome />} />
        <Route
          path="/opportunities"
          element={
            <PrivateRoute>
              <OpportunitiesHome />
            </PrivateRoute>
          }
        />
        <Route path="/store/:slug" element={<CommunityHome />} />
        <Route
          path="/stores/register"
          element={
            <PrivateRoute>
              <StoreRegister />
            </PrivateRoute>
          }
        />

        <Route
          path="/binder"
          element={
            <PrivateRoute>
              <BinderHome />
            </PrivateRoute>
          }
        />
        <Route path="/u/:userIdOrHandle" element={<PublicBinder />} />
        <Route path="/binder/:userIdOrHandle/:sharedCollectionId" element={<PublicBinder binderOnly />} />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfileHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/trades"
          element={
            <PrivateRoute>
              <TradesHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <PrivateRoute>
              <MessagesHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <NotificationsHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/:slug/*"
          element={
            <PrivateRoute allowedRoles={["super_admin", "store_admin"]}>
              <AdminLayout />
            </PrivateRoute>
          }
        />
        <Route
          path="/super-admin"
          element={
            <PrivateRoute allowedRoles={["super_admin"]}>
              <SuperAdminDashboard />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
